import React, { ChangeEvent, useState } from 'react';

import { action, runInAction } from 'mobx';

import capitalize from 'lodash/capitalize';

import Button from '@vromo/ui/components/Button';
import Select from '@vromo/ui/components/Select';
import { useModals } from '@vromo/ui/components/Modals';
import { useStore } from '@vromo/mobx-react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { update } from 'serializr';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import ProfileImage from '@vromo/ui/components/ProfileImage';
import { RootStore } from '../stores/RootStore';
import { Role } from '@vromo/models';
import { EntityList } from '@vromo/models';
import theme from '@vromo/ui/components/theme';
import { UserAccessPosition, userAccessByPosition, UserAccessTypes } from "@vromo/api"
import { useToasts } from '@vromo/ui/components/Toasts';
import { qaLabelTeams } from './../../../../common-qa-labels/teams';
import { buildDynamicLabel } from './../../../../common-qa-labels/utils';

type UserRowProps = {
    userRole: Role;
    roles: EntityList<Role>;
};

/** hide freemium details from FE */
const userAccessName = (id: number) => {
    const access = userAccessByPosition(id!)
    if ((["admin", "freemiumAdmin"] as UserAccessTypes[]).includes(access))
        return "Admin"
    if ((["user", "freemiumUser"] as UserAccessTypes[]).includes(access))
        return "User"
    return "Client"
}

export default observer<UserRowProps>(function UserRow({ userRole, roles }) {
    const showToast = useToasts();
    const rootStore = useStore<RootStore>('rootStore');
    const { isFreemium, role, user, graph } = rootStore.authStore;
    const modals = useModals();
    const [editing, setEditing] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [position, setPosition] = useState<number>(userRole.position!.id!);
    const [newPosition, setNewPosition] = useState<number>(userRole.position!.id!);

    const positionsStore = useLocalObservable(() => ({
        get positions() {
            return {
                admin: isFreemium ?
                    UserAccessPosition.freemiumAdmin : UserAccessPosition.admin,
                user: isFreemium ?
                    UserAccessPosition.freemiumUser : UserAccessPosition.user,
                client: UserAccessPosition.client
            } as { [key: string]: number };
        }
    }));


    function edit() {
        setEditing(true);
    }

    function close(e: React.MouseEvent<HTMLButtonElement>) {
        e.stopPropagation();
        setEditing(false);
    }

    async function save(e: React.MouseEvent<HTMLButtonElement>) {
        close(e);
        const postData = new EntityList(Role)
        postData.push({
            position: { id: newPosition },
            user: { contact: { email: userRole.user!.contact!.email } },
            account: { id: userRole.account!.id }
        } as Role)

        const { items: accountRoles } = await graph.root
            .graph()
            .roles(role.id)
            .account()
            .roles()
            .post(postData)

        runInAction(() => {
            update(roles, { items: [userRole], type: 'delete' });
            update(roles, { items: accountRoles, type: 'create' });

            setPosition(accountRoles[0]!.position!.id!);
            setNewPosition(accountRoles[0]!.position!.id!);
        })

    }

    async function deleteUser(e: React.MouseEvent<HTMLButtonElement>) {
        e.stopPropagation();
        if (
            await modals.confirm(
                'Remove User',
                `Are you sure you want to remove ${userRole?.user?.displayName || 'this user'
                } from this group?`
            )
        ) {
            await graph.root
                .graph()
                .roles(role.id)
                .account()
                .roles()
                .delete({ _all_id__in: userRole.id })

            runInAction(() => {
                roles.remove(userRole);
                showToast(`Successfully deleted`);
            })
        }
    }
    const { email, name } = userRole.user!.contact!;

    return (
        <tr data-testid={buildDynamicLabel(qaLabelTeams.userRowPrefix, name?.toString())}
            onClick={edit}
            onMouseOver={() => setIsHovering(true)}
            onMouseOut={() => setIsHovering(false)}
            style={{ cursor: 'pointer', overflowWrap: 'break-word' }}
        >
            <td data-testid={qaLabelTeams.userRowImage} style={{ textAlign: 'center', width: 60 }}>
                <ProfileImage url={userRole.user?.avatar?.url} user={userRole.user} size={34} />
            </td>
            <td data-testid={qaLabelTeams.userRowNameText} style={{ fontWeight: 600 }}>
                {name}
            </td>
            <td data-testid={qaLabelTeams.userRowEmailText}>{email}</td>
            <td data-testid={qaLabelTeams.userRowRoleText}>
                {editing ? (
                    <Select 
                        data-testid={qaLabelTeams.userRowRoleDropdown}
                        className="form-control input-sm"
                        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                            setNewPosition(parseInt(e.target.value))
                        }
                    >
                        {Object.keys(positionsStore.positions).map((it) => (
                            <option
                                key={positionsStore.positions[it]}
                                value={positionsStore.positions[it]}
                                selected={position === positionsStore.positions[it]}
                            >
                                {capitalize(it)}
                            </option>
                        ))}
                    </Select>
                ) : (
                    userAccessName(position!)
                )}
            </td>
            <td data-testid="teams--user-row-actions">
                {editing ? (
                    <>
                        <Button data-testid={qaLabelTeams.userRowCancelBtn} onClick={close} small>
                            Cancel
                        </Button>
                        <Button data-testid={qaLabelTeams.userRowSaveButton} onClick={save} small>
                            Save
                        </Button>
                    </>
                ) : (
                    <>
                        {userRole.user!.id !== user.id ? (
                            <>
                                <Button
                                    small
                                    onClick={deleteUser}
                                    data-testid={qaLabelTeams.userRowDeleteButton}
                                    style={{ boxShadow: 'none', opacity: isHovering ? 1 : 0 }}
                                    color={theme.grey}
                                >
                                    <FontAwesome icon="trash" style={{ color: theme.mediumGrey }} />
                                </Button>
                            </>
                        ) : null}
                    </>
                )}
            </td>
        </tr>
    );
});
